import React, { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getBranches,
  getProducts,
  getBranchInventory,
  adjustInventory,
  transferInventory,
  downloadInventoryTemplate,
  uploadInventoryCSV,
  quickAddStock,
  toggleInventoryMarketplaceVisibility
} from '../../services/api';
import { Package, Plus, Minus, ArrowRightLeft, Search, Upload, Download, FileSpreadsheet, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const Inventario = () => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  
  const [adjustmentData, setAdjustmentData] = useState({
    action: 'add',
    quantity: 0,
  });
  const [transferData, setTransferData] = useState({
    to_branch_id: '',
    quantity: 0,
  });
  const [quickAddQuantity, setQuickAddQuantity] = useState(1);

  useEffect(() => {
    loadData();
  }, [user]);

  useEffect(() => {
    if (selectedBranch) {
      loadInventory();
    }
  }, [selectedBranch]);

  const loadData = async () => {
    if (!user?.business_id) {
      setLoading(false);
      return;
    }

    try {
      const [branchesRes, productsRes] = await Promise.all([
        getBranches(user.business_id),
        getProducts({ business_id: user.business_id, limit: 100 }),
      ]);

      setBranches(branchesRes.data);
      setProducts(productsRes.data);

      if (branchesRes.data.length > 0) {
        setSelectedBranch(branchesRes.data[0].id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadInventory = async () => {
    try {
      const response = await getBranchInventory(selectedBranch);
      setInventory(response.data);
    } catch (error) {
      console.error('Error loading inventory:', error);
      // Don't show error if it's just empty inventory
      if (error.response?.status !== 422) {
        toast.error('Error al cargar inventario');
      }
      setInventory([]);
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();

    try {
      await adjustInventory(selectedProduct.product_id, selectedBranch, adjustmentData);
      toast.success('Inventario ajustado correctamente');
      setShowAdjustModal(false);
      setAdjustmentData({ action: 'add', quantity: 0 });
      loadInventory();
    } catch (error) {
      console.error('Error adjusting inventory:', error);
      toast.error(getErrorMessage(error, 'Error al ajustar inventario'));
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();

    try {
      await transferInventory(
        selectedProduct.product_id,
        selectedBranch,
        transferData.to_branch_id,
        transferData.quantity
      );
      toast.success('Traspaso completado exitosamente');
      setShowTransferModal(false);
      setTransferData({ to_branch_id: '', quantity: 0 });
      loadInventory();
    } catch (error) {
      console.error('Error transferring inventory:', error);
      toast.error(getErrorMessage(error, 'Error al realizar traspaso'));
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    
    try {
      await quickAddStock(selectedBranch, selectedProduct.product_id, quickAddQuantity);
      toast.success(`+${quickAddQuantity} unidades agregadas`);
      setShowQuickAddModal(false);
      setQuickAddQuantity(1);
      loadInventory();
    } catch (error) {
      console.error('Error quick adding stock:', error);
      toast.error(getErrorMessage(error, 'Error al agregar stock'));
    }
  };

  const handleToggleMarketplaceVisibility = async (item) => {
    try {
      const newValue = !item.hidden_from_marketplace;
      await toggleInventoryMarketplaceVisibility(item.product_id, selectedBranch, newValue);
      toast.success(newValue ? 'Producto oculto del marketplace' : 'Producto visible en marketplace');
      loadInventory();
    } catch (err) {
      toast.error('Error al cambiar visibilidad');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadInventoryTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_inventario.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Plantilla descargada');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Error al descargar plantilla');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Por favor selecciona un archivo CSV');
      return;
    }

    setUploading(true);
    setUploadResults(null);

    try {
      const response = await uploadInventoryCSV(selectedBranch, file);
      setUploadResults(response.data);
      
      if (response.data.success > 0) {
        toast.success(`${response.data.success} productos actualizados`);
        loadInventory();
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errores encontrados`);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error(getErrorMessage(error, 'Error al subir archivo'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openAdjustModal = (item) => {
    setSelectedProduct(item);
    setShowAdjustModal(true);
  };

  const openTransferModal = (item) => {
    setSelectedProduct(item);
    setShowTransferModal(true);
  };

  const openQuickAddModal = (item) => {
    setSelectedProduct(item);
    setQuickAddQuantity(1);
    setShowQuickAddModal(true);
  };

  const filteredInventory = inventory.filter((item) =>
    (item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.product_sku?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Create combined list: inventory items + products without inventory
  const getFullProductList = () => {
    const inventoryProductIds = new Set(inventory.map(i => i.product_id));
    const productsWithoutInventory = products
      .filter(p => !inventoryProductIds.has(p.id))
      .map(p => ({
        id: `no-inv-${p.id}`,
        product_id: p.id,
        product_name: p.name,
        product_sku: p.sku,
        quantity: 0,
        stock_min: p.stock_min || 0,
        branch_id: selectedBranch,
        hasInventory: false,
        hidden_from_marketplace: false
      }));
    
    const inventoryWithFlag = inventory.map(i => ({ ...i, hasInventory: true }));
    
    return [...inventoryWithFlag, ...productsWithoutInventory].filter(item =>
      (item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.product_sku?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!user?.business_id) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Package className="w-16 h-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No hay negocio configurado</h2>
          <p className="text-slate-500">Primero debes crear tu negocio para gestionar inventario.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (branches.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Package className="w-16 h-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No hay sucursales</h2>
          <p className="text-slate-500">Crea una sucursal primero para gestionar inventario.</p>
        </div>
      </DashboardLayout>
    );
  }

  const fullProductList = getFullProductList();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Inventario</h1>
            <p className="text-slate-600 mt-2">Gestiona el stock de tus productos</p>
          </div>
          
          {/* CSV Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4" />
              Descargar Plantilla
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
              data-testid="upload-csv-btn"
            >
              <Upload className="w-4 h-4" />
              Subir CSV
            </button>
          </div>
        </div>

        {/* Branch Selector */}
        <div className="bg-white p-4 rounded-lg border border-slate-200">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Selecciona Sucursal
          </label>
          <select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
            data-testid="branch-selector"
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="search-inventory"
          />
        </div>

        {/* Inventory Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stock Actual</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stock Mínimo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Marketplace</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fullProductList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-500">
                    No hay productos registrados
                  </td>
                </tr>
              ) : (
                fullProductList.map((item) => {
                  const isLowStock = item.quantity <= (item.stock_min || 0);
                  const isZero = item.quantity === 0;
                  return (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-mono text-slate-900">{item.product_sku}</td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.product_name}</td>
                      <td className="px-6 py-4 text-sm font-bold text-slate-900">{item.quantity}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{item.stock_min || 0}</td>
                      <td className="px-6 py-4 text-sm">
                        {isZero ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                            Sin Stock
                          </span>
                        ) : isLowStock ? (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                            Stock Bajo
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            OK
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => handleToggleMarketplaceVisibility(item)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                            item.hidden_from_marketplace
                              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                          title={item.hidden_from_marketplace ? 'Hacer visible en marketplace' : 'Ocultar del marketplace'}
                        >
                          {item.hidden_from_marketplace ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          {item.hidden_from_marketplace ? 'Oculto' : 'Visible'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button
                          onClick={() => openQuickAddModal(item)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm font-medium transition-colors"
                          title="Agregar stock rápido"
                          data-testid={`quick-add-${item.product_sku}`}
                        >
                          <Plus className="w-4 h-4" />
                          Agregar
                        </button>
                        <button
                          onClick={() => openAdjustModal(item)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm font-medium transition-colors"
                          data-testid={`adjust-${item.product_sku}`}
                        >
                          <Package className="w-4 h-4" />
                          Ajustar
                        </button>
                        {branches.length > 1 && (
                          <button
                            onClick={() => openTransferModal(item)}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-sm font-medium transition-colors"
                            disabled={item.quantity === 0}
                            data-testid={`transfer-${item.product_sku}`}
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                            Traspasar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Add Modal */}
      {showQuickAddModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Agregar Stock Rápido</h2>
              <p className="text-sm text-slate-600 mt-1">{selectedProduct.product_name}</p>
              <p className="text-xs text-slate-500">Stock actual: {selectedProduct.quantity}</p>
            </div>
            <form onSubmit={handleQuickAdd} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad a Agregar</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQuickAddQuantity(Math.max(1, quickAddQuantity - 1))}
                    className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    required
                    value={quickAddQuantity}
                    onChange={(e) => setQuickAddQuantity(parseInt(e.target.value) || 1)}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-center text-xl font-bold"
                  />
                  <button
                    type="button"
                    onClick={() => setQuickAddQuantity(quickAddQuantity + 1)}
                    className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex gap-2 mt-3">
                  {[5, 10, 25, 50, 100].map(qty => (
                    <button
                      key={qty}
                      type="button"
                      onClick={() => setQuickAddQuantity(qty)}
                      className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm"
                    >
                      +{qty}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-sm text-green-700">
                  Nuevo stock: <span className="font-bold">{selectedProduct.quantity + quickAddQuantity}</span>
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
                >
                  Agregar +{quickAddQuantity}
                </button>
                <button
                  type="button"
                  onClick={() => setShowQuickAddModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust Modal */}
      {showAdjustModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Ajustar Inventario</h2>
              <p className="text-sm text-slate-600 mt-1">{selectedProduct.product_name}</p>
              <p className="text-xs text-slate-500">Stock actual: {selectedProduct.quantity}</p>
            </div>
            <form onSubmit={handleAdjust} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Acción</label>
                <select
                  value={adjustmentData.action}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, action: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="add">Agregar (+)</option>
                  <option value="subtract">Restar (-)</option>
                  <option value="set">Establecer (=)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={adjustmentData.quantity}
                  onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                >
                  Ajustar
                </button>
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Traspasar Inventario</h2>
              <p className="text-sm text-slate-600 mt-1">{selectedProduct.product_name}</p>
              <p className="text-xs text-slate-500">Stock disponible: {selectedProduct.quantity}</p>
            </div>
            <form onSubmit={handleTransfer} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sucursal Destino</label>
                <select
                  value={transferData.to_branch_id}
                  onChange={(e) => setTransferData({ ...transferData, to_branch_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Selecciona sucursal...</option>
                  {branches.filter((b) => b.id !== selectedBranch).map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad a Traspasar</label>
                <input
                  type="number"
                  min="1"
                  max={selectedProduct.quantity}
                  required
                  value={transferData.quantity}
                  onChange={(e) => setTransferData({ ...transferData, quantity: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 font-medium transition-colors"
                >
                  Traspasar
                </button>
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload CSV Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Subir Inventario CSV</h2>
              <p className="text-sm text-slate-600 mt-1">
                Actualiza el inventario de múltiples productos a la vez
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Formato del archivo CSV
                </h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>sku</strong>: El SKU del producto (debe existir)</li>
                  <li>• <strong>quantity</strong>: Cantidad a ajustar</li>
                  <li>• <strong>action</strong>: set, add, o subtract</li>
                </ul>
                <button
                  onClick={handleDownloadTemplate}
                  className="mt-3 text-sm text-blue-700 underline hover:text-blue-900"
                >
                  Descargar plantilla de ejemplo
                </button>
              </div>

              {/* File Input */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="hidden"
                  id="csv-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="csv-upload"
                  className={`cursor-pointer ${uploading ? 'opacity-50' : ''}`}
                >
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600">
                    {uploading ? 'Subiendo...' : 'Haz clic para seleccionar archivo CSV'}
                  </p>
                </label>
              </div>

              {/* Upload Results */}
              {uploadResults && (
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <div className="flex-1 bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{uploadResults.success}</p>
                      <p className="text-sm text-green-600">Exitosos</p>
                    </div>
                    <div className="flex-1 bg-red-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-700">{uploadResults.errors?.length || 0}</p>
                      <p className="text-sm text-red-600">Errores</p>
                    </div>
                  </div>
                  
                  {uploadResults.errors?.length > 0 && (
                    <div className="bg-red-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                      <p className="text-sm font-medium text-red-700 mb-1">Errores:</p>
                      {uploadResults.errors.map((err, idx) => (
                        <p key={idx} className="text-xs text-red-600">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadResults(null);
                  }}
                  className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Inventario;
